<template>
  <div class="email-step-editor">
    <shared-step-editor-header
      :enabled="stepStatus"
      :confirmation="unsaved"
      class="email-step-editor__header"
      @exit="$emit('exit')"
      @status="onSetStatus"
    >
      <template #name>
        <shared-step-editor-header-name :name="step.name">
          <img :src="`/wp-content/themes/snappy-theme/assets/images/builders/email.svg`" alt="Email Builder" />
        </shared-step-editor-header-name>
      </template>
      <template #editor>
        <swiper-slider @edit="openEditModal = true" v-if="headerItems.length" :items="headerItems" />
      </template>
      <template #actions>
        <email-step-editor-header-actions
          :unlayer-ready="unlayerReady"
          :saving="saving"
          @edit-email="openEditModal = true"
          @send-email="openSendModal = true"
          @save-email="forceSave = true"
          @preview="onPreview"
        />
      </template>
    </shared-step-editor-header>
    <shared-step-editor-unlayer
      v-if="emailPieces.length"
      display-mode="email"
      :design="design"
      :merge-tags="mergeTags"
      :user="user"
      :forceSave="forceSave"
      :preview="preview"
      :fonts="fonts"
      @save="onSave"
      @ready="unlayerReady = true"
      @updated="unsaved = true"
    />
    <shared-modal-edit-pieces
      v-if="headerItems.length"
      :open="openEditModal"
      :pieces="headerItems"
      :smart-pieces="headerSmartItems"
      title="Email Details"
      @update="updatePieces"
      @close="openEditModal = false"
    />
    <modal-send-email :user="user" :open="openSendModal" @confirm="onSendEmail" @close="openSendModal = false" />
    <span v-html="hideScroll"></span>
  </div>
</template>

<script>
import SharedStepEditorHeader from '../shared/SharedStepEditorHeader';
import EmailStepEditorHeaderActions from './EmailStepEditorHeaderActions';
import SharedStepEditorHeaderName from '../shared/SharedStepEditorHeaderName';
import SharedStepEditorUnlayer from '../shared/unlayer/SharedStepEditorUnlayer.vue';
import SharedModalEditPieces from '../shared/modal/SharedModalEditPieces';
import SwiperSlider from '../shared/swiper/SwiperSlider';
import ModalSendEmail from './modals/ModalSendEmail';
import {sendCampaignStepTestEmail} from '../../../../utility/api/campaign/sendCampaignStepTestEmail';
import {unlayerEmailMergeTags} from './unlayerEmailMergeTags';
import {builderMixin} from '../shared/mixins/builder.mixin.js';
import {getCampaignStepPieces} from '../../../../utility/api/campaign/getCampaignStepPieces';

export default {
  name: 'EmailStepEditor',
  mixins: [builderMixin],
  components: {
    SharedStepEditorHeader,
    EmailStepEditorHeaderActions,
    SharedStepEditorHeaderName,
    SwiperSlider,
    ModalSendEmail,
    SharedModalEditPieces,
    SharedStepEditorUnlayer,
  },
  props: {
    step: Object,
    campaign: Object,
    user: Object,
    stepStatus: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    pieces: [],
    saving: false,
    pieceTitles: {
      from_name: 'From name',
      from_email: 'From email',
      reply_email: 'Reply email',
    },
    smartPieceTitles: {
      subject: 'Subject',
    },
    hideScroll: `<style>body {overflow: hidden}</style>`,
    openEditModal: false,
    openSendModal: false,
    unlayerReady: false,
    forceSave: false,
    preview: false,
    unsaved: false,
    emailPieces: [],
    mergeTags: unlayerEmailMergeTags,
    enabled: false,
  }),
  computed: {
    fonts() {
      return this.step.fonts
    },
    headerItems() {
      return this.emailPieces.reduce((acc, piece) => {
        if (Object.keys(this.pieceTitles).includes(piece.name)) {
          acc.push({...piece, header: this.pieceTitles[piece.name]});
        }
        return acc;
      }, []);
    },
    headerSmartItems() {
      return this.emailPieces.reduce((acc, piece) => {
        if (Object.keys(this.smartPieceTitles).includes(piece.name)) {
          acc.push({...piece, header: this.smartPieceTitles[piece.name]});
        }
        return acc;
      }, []);
    },
    design() {
      const designPiece = this.emailPieces?.find(piece => piece.name === 'body');
      return designPiece?.data?.design ? this.parseData(designPiece.data.design) : null;
    },
  },
  methods: {
    onPreview() {
      this.preview = true;
      setTimeout(() => (this.preview = false));
    },
    async getPieces() {
      const {data} = await getCampaignStepPieces(this.campaign.id, this.step.id);
      this.pieces = [...data];
    },
    async onSendEmail(data) {
      try {
        await sendCampaignStepTestEmail(this.campaign.id, this.step.id, {
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
        });

        $.notify('Email has been sent.', 'success');
      } catch (error) {
        $.notify('Failed to send an email.', 'error');
      }

      this.openSendModal = false;
    },
    updatePieces(newPieces) {
      this.updateHeaderPieces(newPieces);
      this.openEditModal = false;
    },

    updateHeaderPieces(headerPieces) {
      this.emailPieces = this.emailPieces.map(piece =>
        Object.keys(headerPieces).includes(piece.name) ? {...piece, content: headerPieces[piece.name]} : piece
      );
      this.unsaved = true;
    },
    isEmailBody(piece) {
      return piece.name === 'body';
    },
    updateBodyPiece(data) {
      const {design, html} = data;
      this.emailPieces = this.emailPieces.map(piece => {
        return this.isEmailBody(piece) ? {...piece, content: html, data: {design: JSON.stringify(design)}} : {...piece};
      });
    },

    async updateDB() {
      const step = {
        pieces: this.emailPieces,
        id: this.step.id,
        status: this.enabled,
      };
      await this.updateStep(step);
    },

    async onSave(data) {
      this.saving = true;
      try {
        this.updateBodyPiece(data);
        await this.updateDB();
        this.forceSave = false;
        this.unsaved = false;
        this.$emit('step-completed');
        this.$emit('exit');
      } catch (err) {
        $.notify('Something went wrong! Saving Failed!', 'error');
      } finally {
        this.saving = false;
      }
    },
    parseData(data) {
      try {
        return JSON.parse(data);
      } catch (e) {
        return null;
      }
    },
  },
  watch: {
    pieces: {
      immediate: true,
      handler(val) {
        this.emailPieces = [...val];
      },
    },
  },
  async mounted() {
    await this.getPieces();
  },
};
</script>

<style scoped lang="scss">
@import '../../../../../sass/web/_variables';

.email-step-editor {
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-template-columns: 1fr;
  min-height: calc(100vh - 30px);
  color: $white-color;
  &__header {
    align-items: center;
  }
}
</style>
